import React, { useState, useRef, useEffect } from "react";
import Header from "../../components/Header";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";

// Style pour les placeholders sur fond sombre
const placeholderStyle = { color: '#9ca3af' };

const Metadatas = ({ onNext }) => {
  const [originalImage, setOriginalImage] = useState(null);
  const [optimizedImage, setOptimizedImage] = useState(null);
  const [imageFileName, setImageFileName] = useState("");
  const [imageExtension, setImageExtension] = useState("");
  const [artists, setArtists] = useState([]);
  const [isLoadingArtists, setIsLoadingArtists] = useState(true);
  const [collections, setCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState("");
  const [formData, setFormData] = useState({
    // Métadonnées JSON
    name: "",
    description: "",
    artistId: "",
    artworkSigned: "no",
    rarety: "",
    variant: "",
    artworkResolution: "",
    artworkFormat: "",
    artistPseudo: "",
    artistName: "",
    artistFirstName: "",
    collectionName: "",
    collectionDescription: "",
    tradeMark: "The Gallery TM",
    creationDate: "",
    // Request JSON
    copyAmount: "",
    price: "",
    multiplier: "",
    royalties: "",
    artistAddress: "",
    artistPaymentSplitterAddress: "",
    priceInEuros: "",
  });

  const originalImageInputRef = useRef(null);
  const optimizedImageInputRef = useRef(null);

  const allowedExtensions = [".jpg", ".jpeg", ".png"];
  const allowedMimeTypes = ["image/jpeg", "image/jpg", "image/png"];

  const getFileExtension = (filename) => {
    const ext = filename.substring(filename.lastIndexOf(".")).toLowerCase();
    return ext;
  };

  const generateRandomHexFileName = () => {
    // Génère 20 caractères hexadécimaux aléatoires
    const hexChars = '0123456789abcdef';
    let result = '';
    for (let i = 0; i < 20; i++) {
      result += hexChars[Math.floor(Math.random() * hexChars.length)];
    }
    return result;
  };

  const handleOriginalImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = getFileExtension(file.name);
    const mimeType = file.type;

    if (!allowedExtensions.includes(ext) || !allowedMimeTypes.includes(mimeType)) {
      alert("Extension non autorisée. Utilisez .jpg, .jpeg ou .png");
      return;
    }

    setOriginalImage(file);
    // Génère un nom de fichier aléatoire de 20 caractères hexadécimaux
    const randomFileName = generateRandomHexFileName();
    setImageFileName(randomFileName);
    setImageExtension(ext);
  };

  const handleOptimizedImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = getFileExtension(file.name);
    const mimeType = file.type;

    if (!allowedExtensions.includes(ext) || !allowedMimeTypes.includes(mimeType)) {
      alert("Extension non autorisée. Utilisez .jpg, .jpeg ou .png");
      return;
    }

    if (originalImage) {
      const originalExt = getFileExtension(originalImage.name);
      if (ext !== originalExt) {
        alert(
          `L'extension de l'image optimisée doit être la même que l'image originale (${originalExt})`
        );
        return;
      }
    }

    setOptimizedImage(file);
  };

  const calculatePrice = (priceInEuros, multiplier) => {
    if (!priceInEuros || !multiplier) return "";
    const priceValue = parseFloat(priceInEuros) * parseFloat(multiplier);
    if (isNaN(priceValue)) return "";

    // Multiplier par 10^18 pour convertir en wei
    // Utiliser une approche qui évite la notation scientifique
    const priceString = priceValue.toString();
    const [integerPart, decimalPart = ""] = priceString.split(".");

    // Calculer la partie entière multipliée par 10^18
    const integerBig = BigInt(integerPart) * BigInt(Math.pow(10, 18));

    // Gérer les décimales si présentes
    let decimalBig = BigInt(0);
    if (decimalPart) {
      const decimalDigits = decimalPart.length;
      const remainingZeros = 18 - decimalDigits;
      if (remainingZeros >= 0) {
        const decimalValue = decimalPart.padEnd(18, "0").substring(0, 18);
        decimalBig = BigInt(decimalValue);
      } else {
        // Trop de décimales, on tronque
        const decimalValue = decimalPart.substring(0, 18);
        decimalBig = BigInt(decimalValue);
      }
    }

    const totalWei = integerBig + decimalBig;
    return totalWei.toString();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Validation pour priceInEuros : ne pas accepter de valeurs < 1
    if (name === "priceInEuros" && value !== "" && parseFloat(value) < 1) {
      return; // Ignorer la valeur si elle est < 1
    }

    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: value,
      };

      // Calculer automatiquement le price si priceInEuros ou multiplier change
      if (name === "priceInEuros" || name === "multiplier") {
        newData.price = calculatePrice(
          name === "priceInEuros" ? value : newData.priceInEuros,
          name === "multiplier" ? value : newData.multiplier
        );
      }

      return newData;
    });
  };

  // Charger tous les artistes au montage du composant
  useEffect(() => {
    const fetchAllArtists = async () => {
      try {
        const artistRef = collection(db, "artists");
        const querySnapshot = await getDocs(artistRef);

        const artistsList = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.artistId) {
            artistsList.push({
              artistId: data.artistId,
              firstName: data.firstName || "",
              lastName: data.lastName || "",
              walletAddress: data.walletAddress || "",
              paymentSplitterAddress: data.paymentSplitterAddress || "",
            });
          }
        });

        // Trier les artistes par artistId
        artistsList.sort((a, b) => a.artistId.localeCompare(b.artistId));
        setArtists(artistsList);
        setIsLoadingArtists(false);
      } catch (error) {
        console.error("Erreur lors de la récupération de la liste des artistes:", error);
        setIsLoadingArtists(false);
      }
    };

    fetchAllArtists();
  }, []);

  // Récupérer les données de l'artiste depuis Firebase quand artistId change
  useEffect(() => {
    const fetchArtistData = async () => {
      if (!formData.artistId || formData.artistId.trim() === "") {
        return;
      }

      try {
        const artistRef = collection(db, "artists");
        const q = query(artistRef, where("artistId", "==", formData.artistId));
        const querySnapshot = await getDocs(q);

        let artistData = null;
        let artistDocId = null;
        querySnapshot.forEach((doc) => {
          artistData = doc.data();
          artistDocId = doc.id;
        });

        if (artistData) {
          // Récupérer toutes les collections de l'artiste depuis la sous-collection
          const collectionsList = [];
          if (artistDocId) {
            try {
              const collectionsRef = collection(db, "artists", artistDocId, "collections");
              const collectionsSnapshot = await getDocs(collectionsRef);

              collectionsSnapshot.forEach((collectionDoc) => {
                collectionsList.push({
                  id: collectionDoc.id,
                  ...collectionDoc.data(),
                });
              });

              setCollections(collectionsList);

              // Sélectionner automatiquement la première collection si disponible
              if (collectionsList.length > 0) {
                const firstCollection = collectionsList[0];
                setSelectedCollectionId(firstCollection.id);
                setFormData((prev) => ({
                  ...prev,
                  collectionName: firstCollection.name || firstCollection.collectionName || "",
                  collectionDescription: firstCollection.description || firstCollection.collectionDescription || "",
                }));
              } else {
                setSelectedCollectionId("");
                setFormData((prev) => ({
                  ...prev,
                  collectionName: "",
                  collectionDescription: "",
                }));
              }
            } catch (error) {
              console.warn("Erreur lors de la récupération des collections:", error);
              setCollections([]);
              setSelectedCollectionId("");
            }
          }

          // Pré-remplir les champs du formulaire avec les données de l'artiste
          setFormData((prev) => ({
            ...prev,
            artistFirstName: artistData.firstName || "",
            artistName: artistData.lastName || "",
            artistAddress: artistData.walletAddress || "",
            artistPaymentSplitterAddress: artistData.paymentSplitterAddress || "",
            // Convertir artistId (ex: "Abadjaye-Justin") en pseudo (ex: "Abadjaye Justin")
            artistPseudo: artistData.artistId ? artistData.artistId.replaceAll("-", " ") : "",
          }));
        } else {
          console.warn(`Aucun artiste trouvé avec l'ID: ${formData.artistId}`);
          setCollections([]);
          setSelectedCollectionId("");
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données de l'artiste:", error);
        setCollections([]);
        setSelectedCollectionId("");
      }
    };

    fetchArtistData();
  }, [formData.artistId]);

  // Gérer le changement de collection sélectionnée
  const handleCollectionChange = (e) => {
    const collectionId = e.target.value;
    setSelectedCollectionId(collectionId);

    if (collectionId) {
      const selectedCollection = collections.find((col) => col.id === collectionId);
      if (selectedCollection) {
        setFormData((prev) => ({
          ...prev,
          collectionName: selectedCollection.name || selectedCollection.collectionName || "",
          collectionDescription: selectedCollection.description || selectedCollection.collectionDescription || "",
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        collectionName: "",
        collectionDescription: "",
      }));
    }
  };

  const downloadFile = (content, filename, contentType) => {
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateFiles = async () => {
    // Validation
    if (!originalImage || !optimizedImage) {
      alert("Veuillez uploader les deux images");
      return;
    }

    if (!imageFileName) {
      alert("Veuillez uploader l'image originale pour générer le nom de fichier");
      return;
    }

    const ext = getFileExtension(originalImage.name);
    const optimizedFileName = `${imageFileName}_optimized${ext}`;
    const originalFileName = `${imageFileName}${ext}`;
    const metadatasFileName = `${imageFileName}_metadatas.json`;

    // Génération du JSON métadonnées
    const metadatasJson = {
      name: formData.name,
      description: formData.description,
      "artist-id": formData.artistId,
      attributes: [
        {
          trait_type: "artwork-signed",
          value: formData.artworkSigned,
        },
        {
          trait_type: "rarety",
          value: formData.rarety,
        },
        {
          trait_type: "variant",
          value: formData.variant,
        },
        {
          trait_type: "artwork-resolution",
          value: formData.artworkResolution,
        },
        {
          trait_type: "artwork-format",
          value: formData.artworkFormat,
        },
        {
          trait_type: "artist-pseudo",
          value: formData.artistPseudo,
        },
        {
          trait_type: "artist-name",
          value: formData.artistName,
        },
        {
          trait_type: "artist-firstName",
          value: formData.artistFirstName,
        },
        {
          trait_type: "collection-name",
          value: formData.collectionName,
        },
        {
          trait_type: "collection-description",
          value: formData.collectionDescription,
        },
        {
          trait_type: "tradeMark",
          value: formData.tradeMark,
        },
        {
          display_type: "date",
          trait_type: "Creation date",
          value: formData.creationDate
            ? new Date(formData.creationDate).getTime()
            : Date.now(),
        },
      ],
    };

    // Créer un FormData pour l'upload
    const formDataToSend = new FormData();

    // Ajouter les images
    formDataToSend.append("files", originalImage, originalFileName);
    formDataToSend.append("files", optimizedImage, optimizedFileName);

    // Créer le fichier JSON des métadonnées et l'ajouter
    const metadatasBlob = new Blob([JSON.stringify(metadatasJson, null, 2)], {
      type: "application/json",
    });
    formDataToSend.append("files", metadatasBlob, metadatasFileName);

    // Ajouter le dossier (optionnel, vous pouvez utiliser imageFileName comme nom de dossier)
    formDataToSend.append("folder", imageFileName);

    // Préparer les données à passer à MintProgress
    const mintRequestData = {
      s3ImgName: originalFileName,
      s3MetadatasName: metadatasFileName,
      copyAmount: formData.copyAmount,
      price: formData.price,
      priceInEuros: formData.priceInEuros ? formData.priceInEuros.toString() : "",
      royalties: formData.royalties,
      artistAddress: formData.artistAddress,
      artistPaymentSplitterAddress: formData.artistPaymentSplitterAddress,
    };

    // Console logs pour debug
    console.log("=== MÉTADONNÉES JSON ===");
    console.log(JSON.stringify(metadatasJson, null, 2));
    console.log("=== REQUÊTE MINT ===");
    console.log(JSON.stringify(mintRequestData, null, 2));

    // Passer les données au Router pour MintProgress
    if (onNext) {
      onNext({
        originalImage,
        optimizedImage,
        imageFileName,
        ext,
        originalFileName,
        optimizedFileName,
        metadatasFileName,
        metadatasJson,
        mintRequestData,
        formDataToSend,
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#00263a]">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-3xl font-bold text-white mb-6">Génération de Métadonnées</h1>

        {/* Section Upload Images */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">
            Upload des Images
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Image Originale *
              </label>
              <input
                type="file"
                ref={originalImageInputRef}
                accept=".jpg,.jpeg,.png"
                onChange={handleOriginalImageChange}
                className="block w-full text-sm text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#33c7b6] file:text-[#00263a] hover:file:bg-[#2ab3a3] cursor-pointer"
              />
              {originalImage && (
                <p className="mt-2 text-sm text-gray-300">
                  {originalImage.name}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Image Optimisée *
              </label>
              <input
                type="file"
                ref={optimizedImageInputRef}
                accept=".jpg,.jpeg,.png"
                onChange={handleOptimizedImageChange}
                className="block w-full text-sm text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#33c7b6] file:text-[#00263a] hover:file:bg-[#2ab3a3] cursor-pointer"
              />
              {optimizedImage && (
                <p className="mt-2 text-sm text-gray-300">
                  {optimizedImage.name}
                </p>
              )}
            </div>
          </div>
          {imageFileName && imageExtension && (
            <div className="mt-4 text-sm text-gray-300">
              <p>
                Nom de fichier généré: <strong className="text-white">{imageFileName}{imageExtension}</strong>
              </p>
              {optimizedImage && (
                <p className="mt-1">
                  Image optimisée: <strong className="text-white">{imageFileName}_optimized{imageExtension}</strong>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Formulaire Métadonnées */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">
            Métadonnées JSON
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Artwork Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Artist ID *
              </label>
              <select
                name="artistId"
                value={formData.artistId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                style={{ colorScheme: 'dark' }}
                disabled={isLoadingArtists}
              >
                <option value="" className="bg-gray-700">
                  {isLoadingArtists ? "Chargement des artistes..." : "Sélectionnez un artiste"}
                </option>
                {artists.map((artist) => (
                  <option key={artist.artistId} value={artist.artistId} className="bg-gray-700">
                    {artist.artistId.replaceAll("-", " ")}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-white mb-1">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Artwork Signed
              </label>
              <select
                name="artworkSigned"
                value={formData.artworkSigned}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                style={{ colorScheme: 'dark' }}
              >
                <option value="no" className="bg-gray-700">No</option>
                <option value="yes" className="bg-gray-700">Yes</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Rarety *
              </label>
              <select
                name="rarety"
                value={formData.rarety}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                style={{ colorScheme: 'dark' }}
              >
                <option value="" className="bg-gray-700">Sélectionner une rareté</option>
                <option value="COMMON" className="bg-gray-700">COMMON</option>
                <option value="UNCOMMON" className="bg-gray-700">UNCOMMON</option>
                <option value="RARE" className="bg-gray-700">RARE</option>
                <option value="EXCLUSIVE" className="bg-gray-700">EXCLUSIVE</option>
                <option value="ONE" className="bg-gray-700">ONE</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Variant *
              </label>
              <input
                type="text"
                name="variant"
                value={formData.variant}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                placeholder="ex: Genesys"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Artwork Resolution *
              </label>
              <input
                type="text"
                name="artworkResolution"
                value={formData.artworkResolution}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                placeholder="ex: 3756 x 2336"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Artwork Format *
              </label>
              <select
                name="artworkFormat"
                value={formData.artworkFormat}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                style={{ colorScheme: 'dark' }}
              >
                <option value="" className="bg-gray-700">Sélectionner un format</option>
                <option value="Landscape" className="bg-gray-700">Landscape</option>
                <option value="Portrait" className="bg-gray-700">Portrait</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Artist Pseudo *
              </label>
              <input
                type="text"
                name="artistPseudo"
                value={formData.artistPseudo}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Artist Name *
              </label>
              <input
                type="text"
                name="artistName"
                value={formData.artistName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Artist First Name *
              </label>
              <input
                type="text"
                name="artistFirstName"
                value={formData.artistFirstName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Collection *
              </label>
              <select
                value={selectedCollectionId}
                onChange={handleCollectionChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                style={{ colorScheme: 'dark' }}
                disabled={!formData.artistId || collections.length === 0}
              >
                <option value="" className="bg-gray-700">
                  {!formData.artistId
                    ? "Sélectionnez d'abord un artiste"
                    : collections.length === 0
                      ? "Aucune collection disponible"
                      : "Sélectionnez une collection"}
                </option>
                {collections.map((col) => (
                  <option key={col.id} value={col.id} className="bg-gray-700">
                    {col.name || col.collectionName || col.id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Collection Name * (Rempli automatiquement)
              </label>
              <input
                type="text"
                name="collectionName"
                value={formData.collectionName}
                readOnly
                className="w-full px-3 py-2 border border-gray-600 bg-gray-600 text-gray-400 rounded-md cursor-not-allowed"
                title="Rempli automatiquement depuis la collection sélectionnée"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-white mb-1">
                Collection Description * (Rempli automatiquement)
              </label>
              <textarea
                name="collectionDescription"
                value={formData.collectionDescription}
                readOnly
                rows="3"
                className="w-full px-3 py-2 border border-gray-600 bg-gray-600 text-gray-400 rounded-md cursor-not-allowed"
                title="Rempli automatiquement depuis la collection sélectionnée"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Trade Mark
              </label>
              <input
                type="text"
                name="tradeMark"
                value={formData.tradeMark}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Creation Date *
              </label>
              <input
                type="datetime-local"
                name="creationDate"
                value={formData.creationDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
          </div>
        </div>

        {/* Formulaire Request JSON */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">
            Request JSON
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Copy Amount *
              </label>
              <input
                type="text"
                name="copyAmount"
                value={formData.copyAmount}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Multiplicateur (Taux Euro/Polygon) *
              </label>
              <input
                type="number"
                step="0.0001"
                name="multiplier"
                value={formData.multiplier}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                placeholder="ex: 10"
              />
              <p className="mt-1 text-xs text-gray-300 italic">
                Taux de conversion Euro/Polygon. Si 1 euro = 0.1 polygon, le multiplicateur = 10
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Price in Euros *
              </label>
              <input
                type="number"
                step="0.01"
                min="1"
                name="priceInEuros"
                value={formData.priceInEuros}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                placeholder="ex: 85"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Price (wei) - Calculé automatiquement
              </label>
              <input
                type="text"
                name="price"
                value={formData.price || ""}
                readOnly
                className="w-full px-3 py-2 border border-gray-600 rounded-md bg-gray-700 text-white cursor-not-allowed"
                placeholder={formData.priceInEuros && formData.multiplier ? "Calcul en cours..." : "Calculé automatiquement"}
              />
              {formData.price && (
                <p className="mt-1 text-xs text-gray-300">
                  {formData.priceInEuros && formData.multiplier
                    ? `${formData.priceInEuros} € × ${formData.multiplier} × 10^18 = ${formData.price}`
                    : ""}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Royalties *
              </label>
              <input
                type="text"
                name="royalties"
                value={formData.royalties}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                placeholder="ex: 1000"
              />
              <p className="mt-1 text-xs text-gray-300 italic">
                1000 = 10%
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Artist Address *
              </label>
              <input
                type="text"
                name="artistAddress"
                value={formData.artistAddress}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                placeholder="0x..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Artist Payment Splitter Address
              </label>
              <input
                type="text"
                name="artistPaymentSplitterAddress"
                value={formData.artistPaymentSplitterAddress}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#33c7b6]"
                placeholder="0x..."
              />
              <p className="mt-1 text-xs text-gray-300 italic">
                Si vous avez déjà l'adresse du payment splitter, insérez-la ici. Sinon, elle sera créée automatiquement.
              </p>
            </div>
          </div>
        </div>

        {/* Bouton Générer */}
        <div className="flex justify-center">
          <button
            onClick={generateFiles}
            className="bg-[#33c7b6] hover:bg-[#2ab3a3] text-[#00263a] px-8 py-3 rounded-full font-semibold transition-colors"
          >
            Valider et Lancer le Mint
          </button>
        </div>
      </div>
    </div>
  );
};

export default Metadatas;

